import { useCallback, useEffect, useRef, useState } from 'react';
import { createSensorPointCloudManifest, getSensorPointCloudStatus } from '../api/client';
import { Obstacle, Sensor, SensorPointCloudManifestEntry } from '../api/models';
import { loadBinPointCloud } from '../utils/loadBinPointCloud';
import {
  registerCloudPending,
  registerCloudReady,
  resetCloudLoadingScope,
  unregisterCloud,
} from '../utils/cloudLoading';
import { useSceneStore } from '../state/sceneStore';

export const MAX_CONCURRENT_CLOUD_FETCHES = 6;
const STATUS_POLL_INTERVAL_MS = 1000;
const MAX_STATUS_POLL_ATTEMPTS = 300;
const RELOAD_DEBOUNCE_MS = 400;
export { RELOAD_DEBOUNCE_MS };

function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true;
  }
  if (typeof error === 'object' && error !== null && 'code' in error) {
    return (error as { code?: string }).code === 'ERR_CANCELED';
  }
  return false;
}

export interface DesignPointCloudState {
  sensorId: string;
  cacheKey: string | null;
  url: string | null;
  points: Float32Array | null;
  ready: boolean;
  loading: boolean;
  error: string | null;
}

export interface UseDesignPointCloudsOptions {
  sensors: Sensor[];
  obstacles: Obstacle[];
  pointCloudVisible: boolean;
  isAlignmentMode: boolean;
  isReadOnly: boolean;
  isDragging: boolean;
  projectId?: number | null;
  statusPollIntervalMs?: number;
}

export interface UseDesignPointCloudsResult {
  clouds: DesignPointCloudState[];
  demoPoints: Float32Array | null;
  isLoading: boolean;
}

export interface SensorChangeDiff {
  changedIds: string[];
  addedIds: string[];
  removedIds: string[];
}

type ReloadMode = 'full' | 'partial';

function buildSingleSensorKey(sensor: Sensor): string {
  const params = sensor.parameters ? JSON.stringify(sensor.parameters) : '';
  return [
    sensor.id,
    sensor.type,
    sensor.rotationRate,
    sensor.position.join(','),
    sensor.rotation.join(','),
    params,
  ].join(':');
}

function buildSensorKeysMap(sensors: Sensor[]): Map<string, string> {
  return new Map(sensors.map((sensor) => [sensor.id, buildSingleSensorKey(sensor)]));
}

export function diffSensorChanges(
  sensors: Sensor[],
  prevMap: Map<string, string>,
): SensorChangeDiff {
  const currentMap = buildSensorKeysMap(sensors);
  const changedIds: string[] = [];
  const addedIds: string[] = [];
  const removedIds: string[] = [];

  for (const sensor of sensors) {
    const prevKey = prevMap.get(sensor.id);
    if (prevKey === undefined) {
      addedIds.push(sensor.id);
    } else if (prevKey !== currentMap.get(sensor.id)) {
      changedIds.push(sensor.id);
    }
  }

  Array.from(prevMap.keys()).forEach((id) => {
    if (!currentMap.has(id)) {
      removedIds.push(id);
    }
  });

  return { changedIds, addedIds, removedIds };
}

function buildObstacleSceneKey(obstacles: Obstacle[]): string {
  return obstacles
    .map((obstacle) =>
      [
        obstacle.id,
        obstacle.position.join(','),
        obstacle.size.join(','),
        obstacle.rotation.join(','),
      ].join(':'),
    )
    .sort()
    .join('|');
}

function createInitialCloudState(entry: SensorPointCloudManifestEntry): DesignPointCloudState {
  return {
    sensorId: entry.sensorId,
    cacheKey: entry.cacheKey ?? null,
    url: entry.url ?? null,
    points: null,
    ready: entry.ready,
    loading: Boolean(entry.url) && entry.ready,
    error: null,
  };
}

function applyPartialInvalidation(
  current: DesignPointCloudState[],
  diff: SensorChangeDiff,
): DesignPointCloudState[] {
  const removedSet = new Set(diff.removedIds);
  const changedSet = new Set(diff.changedIds);

  let next = current.filter((cloud) => !removedSet.has(cloud.sensorId));

  next = next.map((cloud) =>
    changedSet.has(cloud.sensorId)
      ? { ...cloud, points: null, loading: true, error: null, ready: false }
      : cloud,
  );

  for (const id of diff.addedIds) {
    if (!next.some((cloud) => cloud.sensorId === id)) {
      next.push({
        sensorId: id,
        cacheKey: null,
        url: null,
        points: null,
        ready: false,
        loading: true,
        error: null,
      });
    }
  }

  return next;
}

function mergeManifestIntoClouds(
  sensors: Sensor[],
  current: DesignPointCloudState[],
  manifestEntries: SensorPointCloudManifestEntry[],
  targetSensorIds: Set<string>,
): DesignPointCloudState[] {
  const currentById = new Map(current.map((cloud) => [cloud.sensorId, cloud]));
  const entryById = new Map(manifestEntries.map((entry) => [entry.sensorId, entry]));

  return sensors.map((sensor) => {
    const existing = currentById.get(sensor.id);
    if (!targetSensorIds.has(sensor.id)) {
      return existing ?? {
        sensorId: sensor.id,
        cacheKey: null,
        url: null,
        points: null,
        ready: false,
        loading: false,
        error: null,
      };
    }

    const entry = entryById.get(sensor.id);
    if (!entry) {
      return existing ?? {
        sensorId: sensor.id,
        cacheKey: null,
        url: null,
        points: null,
        ready: false,
        loading: false,
        error: null,
      };
    }

    return {
      ...createInitialCloudState(entry),
      points: null,
      loading: true,
      error: null,
    };
  });
}

async function runBoundedPool<T>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<void>,
): Promise<void> {
  if (items.length === 0) {
    return;
  }

  let nextIndex = 0;
  const workerCount = Math.min(limit, items.length);
  const runners = Array.from({ length: workerCount }, async () => {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      if (currentIndex >= items.length) {
        return;
      }
      await worker(items[currentIndex]);
    }
  });
  await Promise.all(runners);
}

function isEmptyCloudError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  return (
    error.message.includes('Point cloud data is empty') ||
    error.message.includes('Point cloud contains no valid points')
  );
}

export function useDesignPointClouds({
  sensors,
  obstacles,
  pointCloudVisible,
  isAlignmentMode,
  isReadOnly,
  isDragging,
  projectId,
  statusPollIntervalMs = STATUS_POLL_INTERVAL_MS,
}: UseDesignPointCloudsOptions): UseDesignPointCloudsResult {
  const [clouds, setClouds] = useState<DesignPointCloudState[]>([]);
  const [demoPoints, setDemoPoints] = useState<Float32Array | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const generationRef = useRef(0);
  const prevSensorKeysRef = useRef<Map<string, string>>(new Map());
  const prevObstacleKeyRef = useRef('');
  const prevProjectIdRef = useRef(projectId ?? null);
  const prevReloadNonceRef = useRef(useSceneStore.getState().designCloudReloadNonce);
  const loadSettledRef = useRef(true);
  const cloudsRef = useRef(clouds);
  cloudsRef.current = clouds;

  const setDesignCloudLoadError = useSceneStore((state) => state.setDesignCloudLoadError);
  const designCloudReloadNonce = useSceneStore((state) => state.designCloudReloadNonce);

  const forgetLoadedScene = useCallback(() => {
    prevSensorKeysRef.current = new Map();
    prevObstacleKeyRef.current = '';
  }, []);

  const releasePendingSensors = useCallback((sensorIds: string[]) => {
    sensorIds.forEach((id) => unregisterCloud('design', id));
  }, []);

  const resetCloudState = useCallback(() => {
    setClouds([]);
    setDemoPoints(null);
    setIsLoading(false);
    setDesignCloudLoadError(null);
    resetCloudLoadingScope('design');
  }, [setDesignCloudLoadError]);

  useEffect(() => {
    if (designCloudReloadNonce !== prevReloadNonceRef.current) {
      prevReloadNonceRef.current = designCloudReloadNonce;
      forgetLoadedScene();
    }
  }, [designCloudReloadNonce, forgetLoadedScene]);

  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    let abortController: AbortController | null = null;
    let activePendingIds: string[] = [];

    const cleanupActiveLoad = () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
      if (abortController) {
        abortController.abort();
        abortController = null;
      }
      if (activePendingIds.length > 0) {
        releasePendingSensors(activePendingIds);
        activePendingIds = [];
      }
    };

    if (isDragging) {
      return cleanupActiveLoad;
    }

    if (isAlignmentMode || !pointCloudVisible) {
      generationRef.current += 1;
      cleanupActiveLoad();
      resetCloudState();
      forgetLoadedScene();
      return;
    }

    if (prevProjectIdRef.current !== (projectId ?? null)) {
      prevProjectIdRef.current = projectId ?? null;
      forgetLoadedScene();
    }

    const obstacleKey = buildObstacleSceneKey(obstacles);
    const obstaclesChanged = obstacleKey !== prevObstacleKeyRef.current;
    const isFirstLoad = prevSensorKeysRef.current.size === 0 && sensors.length > 0;
    const sensorDiff = diffSensorChanges(sensors, prevSensorKeysRef.current);
    const hasSensorChanges =
      sensorDiff.changedIds.length > 0
      || sensorDiff.addedIds.length > 0
      || sensorDiff.removedIds.length > 0;

    if (!obstaclesChanged && !hasSensorChanges) {
      return cleanupActiveLoad;
    }

    if (sensors.length === 0) {
      generationRef.current += 1;
      cleanupActiveLoad();
      resetCloudState();
      forgetLoadedScene();
      return;
    }

    const reloadMode: ReloadMode =
      obstaclesChanged || isFirstLoad ? 'full' : 'partial';
    const targetSensorIds =
      reloadMode === 'full'
        ? sensors.map((sensor) => sensor.id)
        : [...sensorDiff.changedIds, ...sensorDiff.addedIds];
    const targetSensorIdSet = new Set(targetSensorIds);

    debounceTimer = setTimeout(() => {
      const generation = generationRef.current + 1;
      generationRef.current = generation;
      abortController = new AbortController();
      const signal = abortController.signal;

      const loadClouds = async () => {
        const isPartial = reloadMode === 'partial';
        setDesignCloudLoadError(null);

        if (!isPartial) {
          setClouds([]);
          setDemoPoints(null);
          resetCloudLoadingScope('design');
        } else {
          sensorDiff.removedIds.forEach((id) => unregisterCloud('design', id));
          setClouds((current) => applyPartialInvalidation(current, sensorDiff));
        }

        setIsLoading(true);
        loadSettledRef.current = false;

        // Login/demo uses one pregenerated cloud, not per-sensor fetches.
        // Registering every sensor as pending leaves the toast stuck at 1/N.
        if (isReadOnly) {
          activePendingIds = ['demo'];
          registerCloudPending('design', 'demo');
          try {
            const response = await fetch('/demo_pointcloud.json', { signal });
            if (!response.ok) {
              throw new Error(`Failed to load demo point cloud: ${response.status}`);
            }
            const data = await response.json();
            if (!Array.isArray(data)) {
              throw new Error('Invalid demo point cloud format');
            }
            if (generationRef.current !== generation) {
              return;
            }
            setDemoPoints(new Float32Array(data));
            registerCloudReady('design', 'demo');
          } catch (error) {
            if (generationRef.current !== generation || isAbortError(error)) {
              return;
            }
            registerCloudReady('design', 'demo');
            console.error('Failed to load demo point cloud:', error);
          } finally {
            if (generationRef.current === generation) {
              setIsLoading(false);
              loadSettledRef.current = true;
              prevObstacleKeyRef.current = obstacleKey;
              prevSensorKeysRef.current = buildSensorKeysMap(sensors);
              activePendingIds = [];
            } else {
              releasePendingSensors(['demo']);
            }
          }
          return;
        }

        activePendingIds = [...targetSensorIds];
        targetSensorIds.forEach((sensorId) => registerCloudPending('design', sensorId));

        try {
          const manifest = await createSensorPointCloudManifest(
            { sensors, obstacles },
            projectId,
            signal,
          );
          if (generationRef.current !== generation) {
            return;
          }

          const manifestEntries = manifest.sensors;
          const existingById = new Map(
            cloudsRef.current.map((cloud) => [cloud.sensorId, cloud]),
          );

          if (isPartial) {
            setClouds((current) =>
              mergeManifestIntoClouds(
                sensors,
                current,
                manifestEntries,
                targetSensorIdSet,
              ),
            );
          } else {
            setClouds(manifestEntries.map(createInitialCloudState));
          }

          const manifestSensorIds = new Set(manifestEntries.map((entry) => entry.sensorId));
          if (!isPartial) {
            sensors.forEach((sensor) => {
              if (!manifestSensorIds.has(sensor.id)) {
                registerCloudReady('design', sensor.id);
              }
            });
          }

          manifestEntries.forEach((entry) => {
            if (isPartial && !targetSensorIdSet.has(entry.sensorId)) {
              return;
            }
            registerCloudPending('design', entry.sensorId);
            if (!entry.cacheKey) {
              registerCloudReady('design', entry.sensorId);
            }
          });

          const resolvedProjectId = manifest.projectId ?? projectId ?? null;
          const fetchable = new Map<string, string>();
          const pendingKeys = new Map<string, string>();

          manifestEntries.forEach((entry) => {
            if (!entry.cacheKey) {
              return;
            }
            if (isPartial && !targetSensorIdSet.has(entry.sensorId)) {
              return;
            }

            const existing = existingById.get(entry.sensorId);
            if (
              isPartial
              && existing?.points
              && existing.cacheKey === entry.cacheKey
              && entry.ready
              && entry.url
            ) {
              setClouds((current) =>
                current.map((cloud) =>
                  cloud.sensorId === entry.sensorId
                    ? {
                        ...cloud,
                        ready: true,
                        loading: false,
                        url: entry.url ?? null,
                        cacheKey: entry.cacheKey ?? null,
                      }
                    : cloud,
                ),
              );
              registerCloudReady('design', entry.sensorId);
              return;
            }

            if (entry.ready && entry.url) {
              fetchable.set(entry.cacheKey, entry.url);
              return;
            }
            pendingKeys.set(entry.cacheKey, entry.sensorId);
          });

          const markSensorLoaded = (sensorId: string) => {
            registerCloudReady('design', sensorId);
          };

          const loadCloudByKey = async (cacheKey: string, url: string) => {
            const sensorId = manifestEntries.find((entry) => entry.cacheKey === cacheKey)?.sensorId;
            if (!sensorId) {
              return;
            }

            setClouds((current) =>
              current.map((cloud) =>
                cloud.sensorId === sensorId
                  ? { ...cloud, loading: true, error: null }
                  : cloud,
              ),
            );

            try {
              const points = await loadBinPointCloud(url);
              if (generationRef.current !== generation) {
                return;
              }
              setClouds((current) =>
                current.map((cloud) =>
                  cloud.sensorId === sensorId
                    ? { ...cloud, points, ready: true, loading: false, url, cacheKey }
                    : cloud,
                ),
              );
              markSensorLoaded(sensorId);
            } catch (error) {
              if (generationRef.current !== generation) {
                return;
              }
              if (isEmptyCloudError(error)) {
                setClouds((current) =>
                  current.map((cloud) =>
                    cloud.sensorId === sensorId
                      ? { ...cloud, points: null, ready: true, loading: false, url, cacheKey }
                      : cloud,
                  ),
                );
                markSensorLoaded(sensorId);
                return;
              }
              const message = error instanceof Error ? error.message : 'Failed to load point cloud';
              setClouds((current) =>
                current.map((cloud) =>
                  cloud.sensorId === sensorId
                    ? { ...cloud, loading: false, error: message }
                    : cloud,
                ),
              );
              markSensorLoaded(sensorId);
            }
          };

          await runBoundedPool(
            Array.from(fetchable.entries()),
            MAX_CONCURRENT_CLOUD_FETCHES,
            async ([cacheKey, url]) => loadCloudByKey(cacheKey, url),
          );

          let attempts = 0;
          while (pendingKeys.size > 0 && attempts < MAX_STATUS_POLL_ATTEMPTS) {
            if (generationRef.current !== generation) {
              return;
            }

            await new Promise((resolve) => setTimeout(resolve, statusPollIntervalMs));
            attempts += 1;

            const status = await getSensorPointCloudStatus(
              Array.from(pendingKeys.keys()),
              resolvedProjectId,
              signal,
            );
            if (generationRef.current !== generation) {
              return;
            }

            const newlyReady: Array<[string, string]> = [];
            status.clouds.forEach((cloud) => {
              if (cloud.ready && cloud.url) {
                newlyReady.push([cloud.cacheKey, cloud.url]);
                pendingKeys.delete(cloud.cacheKey);
              }
            });

            if (newlyReady.length > 0) {
              await runBoundedPool(newlyReady, MAX_CONCURRENT_CLOUD_FETCHES, async ([cacheKey, url]) =>
                loadCloudByKey(cacheKey, url),
              );
            }
          }

          if (generationRef.current !== generation) {
            return;
          }

          if (pendingKeys.size > 0) {
            const timeoutMessage = 'Point cloud generation timed out. Please try again.';
            setDesignCloudLoadError(timeoutMessage);
            pendingKeys.forEach((sensorId) => {
              setClouds((current) =>
                current.map((cloud) =>
                  cloud.sensorId === sensorId
                    ? { ...cloud, loading: false, error: timeoutMessage }
                    : cloud,
                ),
              );
              markSensorLoaded(sensorId);
            });
          }
        } catch (error) {
          if (generationRef.current !== generation || isAbortError(error)) {
            return;
          }
          const message = error instanceof Error ? error.message : 'Failed to load point clouds';
          setDesignCloudLoadError(message);
          targetSensorIds.forEach((sensorId) => registerCloudReady('design', sensorId));
          console.error('Failed to load design point clouds:', error);
        } finally {
          if (generationRef.current === generation) {
            setIsLoading(false);
            loadSettledRef.current = true;
            prevObstacleKeyRef.current = obstacleKey;
            prevSensorKeysRef.current = buildSensorKeysMap(sensors);
            activePendingIds = [];
          } else {
            releasePendingSensors(targetSensorIds);
          }
        }
      };

      void loadClouds();
    }, RELOAD_DEBOUNCE_MS);

    return () => {
      generationRef.current += 1;
      cleanupActiveLoad();
    };
  }, [
    sensors,
    obstacles,
    pointCloudVisible,
    isAlignmentMode,
    isReadOnly,
    isDragging,
    projectId,
    forgetLoadedScene,
    resetCloudState,
    releasePendingSensors,
    setDesignCloudLoadError,
    statusPollIntervalMs,
  ]);

  return {
    clouds,
    demoPoints,
    isLoading,
  };
}
